'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  Edit,
  Eye,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/utils/trpc';
import { BulkRecurringForm } from './bulk-recurring-form';
import { RecurringForm } from './recurring-form';
import { RecurringPreview } from './recurring-preview';

interface RecurringListProps {
  showInactive?: boolean;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

function formatFrequency(
  frequency: string,
  dayOfMonth?: number,
  dayOfWeek?: number
) {
  const frequencyMap = {
    daily: 'Diário',
    weekly: 'Semanal',
    biweekly: 'Quinzenal',
    monthly: 'Mensal',
    quarterly: 'Trimestral',
    yearly: 'Anual',
  };

  let label = frequencyMap[frequency as keyof typeof frequencyMap] || frequency;

  if (frequency === 'monthly' && dayOfMonth) {
    label += ` (dia ${dayOfMonth})`;
  } else if (frequency === 'weekly' && dayOfWeek !== undefined) {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    label += ` (${days[dayOfWeek]})`;
  }

  return label;
}

function formatNextDate(nextDate: string) {
  const date = new Date(nextDate);
  const today = new Date();
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Amanhã';
  if (diffDays < 0) return `${Math.abs(diffDays)} dias atrás`;
  if (diffDays <= 7) return `Em ${diffDays} dias`;

  return date.toLocaleDateString('pt-BR', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}

export function RecurringList({ showInactive = false }: RecurringListProps) {
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const recurringTransactions = useQuery(
    trpc.recurring.list.queryOptions({
      includeInactive: showInactive,
    })
  );

  const toggleActiveMutation = useMutation(
    trpc.recurring.toggleActive.mutationOptions({
      onSuccess: () => {
        recurringTransactions.refetch();
      },
    })
  );

  const deleteMutation = useMutation(
    trpc.recurring.delete.mutationOptions({
      onSuccess: () => {
        recurringTransactions.refetch();
      },
    })
  );

  const { data, isPending } = recurringTransactions;

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Transações Recorrentes</CardTitle>
          <CardDescription>
            Carregando suas transações automáticas...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div className="flex items-center space-x-4 py-3" key={i}>
                <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                </div>
                <div className="h-4 w-20 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeRecurring = data?.filter((r) => r.isActive) || [];
  const inactiveRecurring = data?.filter((r) => !r.isActive) || [];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transações Recorrentes</CardTitle>
              <CardDescription>
                {activeRecurring.length} ativas • {inactiveRecurring.length}{' '}
                pausadas
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                className="gap-2"
                onClick={() => setShowBulkForm(true)}
                variant="outline"
              >
                <Plus className="h-4 w-4" />
                Adicionar Vários
              </Button>
              <Button className="gap-2" onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4" />
                Nova Recorrência
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!data || data.length === 0 ? (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-muted">
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mb-2 font-semibold text-lg">
                Nenhuma transação recorrente
              </h3>
              <p className="mb-4 text-muted-foreground">
                Automatize suas receitas e gastos fixos como salário, aluguel,
                assinaturas e contas.
              </p>
              <Button onClick={() => setShowForm(true)}>
                Criar primeira recorrência
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Active Recurring Transactions */}
              {activeRecurring.length > 0 && (
                <div>
                  <h4 className="mb-3 flex items-center gap-2 font-medium">
                    <Play className="h-4 w-4 text-green-600" />
                    Ativas ({activeRecurring.length})
                  </h4>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Frequência</TableHead>
                          <TableHead>Próxima</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                          <TableHead className="w-[50px]" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeRecurring.map((recurring) => (
                          <TableRow key={recurring.id}>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                {recurring.type === 'credit' ? (
                                  <ArrowUpCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <ArrowDownCircle className="h-4 w-4 text-red-600" />
                                )}
                                <Badge
                                  className={
                                    recurring.type === 'credit'
                                      ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                                      : 'bg-red-500/20 text-red-700 dark:text-red-300'
                                  }
                                  variant={
                                    recurring.type === 'credit'
                                      ? 'default'
                                      : 'secondary'
                                  }
                                >
                                  {recurring.type === 'credit'
                                    ? 'Receita'
                                    : 'Gasto'}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {recurring.description}
                              </div>
                              <div className="text-muted-foreground text-sm">
                                Criada em{' '}
                                {new Date(
                                  recurring.createdAt
                                ).toLocaleDateString('pt-BR')}
                              </div>
                            </TableCell>
                            <TableCell>
                              {recurring.category ? (
                                <Badge variant="outline">
                                  {recurring.category.icon}{' '}
                                  {recurring.category.name}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">
                                  Sem categoria
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {formatFrequency(
                                  recurring.frequency,
                                  recurring.dayOfMonth,
                                  recurring.dayOfWeek
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {formatNextDate(recurring.nextDate)}
                              </div>
                            </TableCell>
                            <TableCell
                              className={`text-right font-medium font-mono ${
                                recurring.type === 'credit'
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {recurring.type === 'credit' ? '+' : '-'}
                              {formatCurrency(Number(recurring.amount))}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    className="h-8 w-8 p-0"
                                    variant="ghost"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                  <DropdownMenuItem
                                    onClick={() => setPreviewId(recurring.id)}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    Ver próximas
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEditingId(recurring.id);
                                      setShowForm(true);
                                    }}
                                  >
                                    <Edit className="mr-2 h-4 w-4" />
                                    Editar
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() =>
                                      toggleActiveMutation.mutate({
                                        id: recurring.id,
                                      })
                                    }
                                  >
                                    <Pause className="mr-2 h-4 w-4" />
                                    Pausar
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-red-600"
                                    onClick={() =>
                                      deleteMutation.mutate({
                                        id: recurring.id,
                                      })
                                    }
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Excluir
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Inactive Recurring Transactions */}
              {showInactive && inactiveRecurring.length > 0 && (
                <div>
                  <h4 className="mb-3 flex items-center gap-2 font-medium">
                    <Pause className="h-4 w-4 text-orange-600" />
                    Pausadas ({inactiveRecurring.length})
                  </h4>
                  <div className="rounded-md border opacity-60">
                    <Table>
                      <TableBody>
                        {inactiveRecurring.map((recurring) => (
                          <TableRow key={recurring.id}>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                {recurring.type === 'credit' ? (
                                  <ArrowUpCircle className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ArrowDownCircle className="h-4 w-4 text-muted-foreground" />
                                )}
                                <div>
                                  <div className="font-medium">
                                    {recurring.description}
                                  </div>
                                  <div className="text-muted-foreground text-sm">
                                    {formatFrequency(
                                      recurring.frequency,
                                      recurring.dayOfMonth,
                                      recurring.dayOfWeek
                                    )}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center gap-2">
                                <span className="font-medium font-mono text-muted-foreground">
                                  {formatCurrency(Number(recurring.amount))}
                                </span>
                                <Button
                                  onClick={() =>
                                    toggleActiveMutation.mutate({
                                      id: recurring.id,
                                    })
                                  }
                                  size="sm"
                                  variant="outline"
                                >
                                  <Play className="mr-1 h-3 w-3" />
                                  Ativar
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <BulkRecurringForm
        onClose={() => setShowBulkForm(false)}
        onSuccess={() => {
          setShowBulkForm(false);
          recurringTransactions.refetch();
        }}
        open={showBulkForm}
      />

      <RecurringForm
        editingId={editingId}
        onClose={() => {
          setShowForm(false);
          setEditingId(null);
        }}
        onSuccess={() => {
          setShowForm(false);
          setEditingId(null);
          recurringTransactions.refetch();
        }}
        open={showForm}
      />

      {previewId && (
        <RecurringPreview
          onClose={() => setPreviewId(null)}
          open={!!previewId}
          recurringId={previewId}
        />
      )}
    </>
  );
}
